import { useEffect, useRef } from "react";

import { useAuth } from "@hl/shared-features/lib/features/auth";

import { CollectionStatus } from "../../../apollo/graphql.generated";
import { useMintPage } from "../../../hooks/useMintPage";
import useMintState, { MintStateRequired } from "../../../hooks/useMintState";
import { SaleStatus } from "../utils/types";

import { useRankedAuctionQuery } from "./ra-queries.graphql.generated";

const bidsLimit = 10;

export const useRankedAuction = () => {
  const { mintVector, collectionStatus, saleStatus, refetch } = useMintState();

  const { data, loading } = useRankedAuctionQuery({
    variables: {
      mintVectorId: mintVector?.id ?? "0",
      bidsLimit,
    },
    skip: !mintVector?.id,
  });

  if (!mintVector) {
    return {};
  }
  const isStarted = !(
    SaleStatus.NOT_STARTED === saleStatus ||
    collectionStatus !== CollectionStatus.LIVE
  );

  return {
    rankedAuction: data?.rankedAuction,
    loading,
    mintVector,
    bidsLimit,
    isStarted,
    refetchRankedAuction: refetch,
  };
};

export const useRankedAuctionAutoRefresh = (refreshPeriodSec = 60) => {
  const { mintVector, collection } = useMintState() as MintStateRequired;
  const { refetchMintVector } = useMintPage(collection?.id ?? "", {
    skipAuction: true,
  });

  const { data, refetch } = useRankedAuctionQuery({
    variables: {
      mintVectorId: mintVector.id!,
      bidsLimit,
    },
    skip: !mintVector.id,
  });
  useEffect(() => {
    const now = new Date();
    if (
      !mintVector ||
      mintVector.paused ||
      !mintVector.end ||
      new Date(mintVector.end) < now // ended
    ) {
      return;
    }
    const interval = setInterval(() => {
      refetch();
    }, refreshPeriodSec * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [mintVector, refetch]);

  const endDate = data?.rankedAuction?.end;

  const origEndDateRef = useRef(endDate);

  useEffect(() => {
    if (!origEndDateRef.current) {
      origEndDateRef.current = endDate;
    }
    if (origEndDateRef.current !== endDate) {
      // end date changed, auction was extended
      console.log("auction was extended", { endDate });
      refetchMintVector();
      origEndDateRef.current = endDate;
    }
  }, [refetchMintVector, endDate]);

  // refetch auction when authenticated state changes
  const { loading: authLoading, authenticated } = useAuth();
  const authRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (authRef.current === null) {
      authRef.current = authenticated;
    } else if (authRef.current !== authenticated) {
      console.log("auth state changed, reload ranked auction");
      authRef.current = authenticated;
      refetch();
    }
  }, [authLoading, authenticated, refetch]);
};
